import React, { useEffect, useMemo, useState } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { getPrimaryColor, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	clearFiltersWorkOrders,
	downloadWorkOrderCSV,
	fetchWorkOrders,
	replaceFiltersAndSortingWorkOrders,
	updateFiltersWorkOrders,
	updatePaginationWorkOrders,
	updateSortingWorkOrders,
} from '../../thunks/work_orders_thunks_wrapper';
import {
	workOrderPrioritiesRestCrudThunksForBuyer,
	workOrderPrioritiesRestCrudThunksForSupplier,
} from '../../thunks/work_order_priorities_thunks';
import {
	assetTypesRestCrudThunksForBuyer,
	assetTypesRestCrudThunksForSupplier,
} from '../../thunks/asset_types_thunks';
import {
	resolutionTypesRestCrudThunksForBuyer,
	resolutionTypesRestCrudThunksForSupplier,
} from '../../thunks/resolution_types_thunks';
import {
	problemTypesHeirarchicalForBuyer,
	problemTypesHeirarchicalForSupplier,
} from '../../thunks/problem_types_thunks';
import {
	fetchSupplierFacilitiesInPrivateNetworkForBuyer,
	fetchSupplierFacilitiesInPrivateNetworkForSupplier,
} from '../../thunks/supplier_facilities_thunks';
import {
	plannedMaintenanceSchedulesRestCrudThunksForBuyer,
	plannedMaintenanceSchedulesRestCrudThunksForSupplier,
} from '../../thunks/planned_maintenance_schedules_thunks';
import { fieldTechsRestCrudThunksForSupplier } from '../../thunks/field_techs_thunks';
import { spendCategoriesRestCrudThunksForSupplier } from '../../thunks/spend_categories_thunks';
import {
	assetsRestCrudThunksForBuyer,
	assetsRestCrudThunksForSupplier,
} from '../../thunks/assets_thunks';
import { ContractorPrivateNetworksRestCrudThunksForSupplier } from '../../thunks/contractors_private_networks_thunks';
import {
	areasRestCrudThunksForBuyer,
	areasRestCrudThunksForSupplier,
	fetchDistinctAreasHeirarchy,
} from '../../thunks/areas_thunks';
import SelectedFilters from '../selected_filters_pills/SelectedFilters';
import { Tag } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import {
	useBuyerFilterConfiguration,
	useCommonFilterConfiguration,
	useDashboardFilterConfiguration,
	useSupplierFilterConfiguration,
} from '../../hooks/workOrderHooks';
import {
	contractorClientsRestCrudThunksForBuyer,
	contractorClientsRestCrudThunksForSupplier,
} from '../../thunks/contractor_clients_thunks';
import {
	contractorLocationsRestCrudThunksForBuyer,
	contractorLocationsRestCrudThunksForSupplier,
} from '../../thunks/contractor_locations_thunks';
import { isBuyerOrInternalTech } from '../../utils/AuthUtils';
import {
	contactsRestCrudThunksForSupplierForThirdPartySuppliers,
	getContactsRestCrudThunksWithInternalTechs,
} from '../../thunks/contacts_thunks';

const TARGETCOLLECTIONNAME = 'workOrdersTable';

const AttentionDashboardFilterConfig = ({
	view,
	setView,
	updateFilters,
	companyConfig,
	currentUser,
	fetchAreasDistinct,
	fetchLocations,
	fetchWorkOrderPriorities,
	fetchProblemTypes,
	fetchSupplierFacilitiesInPrivateNetwork,
	fetchFieldTechs,
	fetchMultipleFieldTechs,
	filterConfig,
	locations,
	areas,
	fieldTechs,
	workOrderPriorities,
	problemTypes,
	supplierFacilities,
	userType,
	spendCategories,
	fetchSpendCategories,
	fetchMultipleSpendCategories,
	assetTypes,
	fetchAssetTypes,
	fetchMultipleAssetTypes,
	resolutionTypes,
	fetchResolutionTypes,
	fetchMultipleResolutionTypes,
	fetchPMSchedule,
	fetchMultiplePMSchedule,
	planned_maintenance_schedules,
	assets,
	fetchAssets,
	fetchMultipleAssets,
	contractorPrivateNetworks,
	fetchContractors,
	contractorClients,
	fetchContractorClients,
	fetchMultipleContractorClients,
	contractorLocations,
	fetchContractorLocations,
	fetchMultipleContractorLocations,
	users,
	fetchUsers,
	fetchMultipleUsers,
}): React.ReactElement => {
	const staleDays = useMemo(
		() => nullSafeGetOrElse('config.workOrder.stale.afterXDays', companyConfig, 14),
		[companyConfig]
	);

	const DASHBOARD_FILTER_CONFIGS = useDashboardFilterConfiguration({ view });
	const BUYER_FILTER_CONFIGS = useBuyerFilterConfiguration({ updateFilters });
	const SUPPLIER_FILTER_CONFIG = useSupplierFilterConfiguration({
		fieldTechs,
		updateFilters,
		fetchFieldTechs,
		fetchMultipleFieldTechs,
		spendCategories,
		fetchSpendCategories,
		fetchMultipleSpendCategories,
		contractorPrivateNetworks,
		fetchContractors,
		currentUser,
		contractorClients,
		fetchContractorClients,
		fetchMultipleContractorClients,
		contractorLocations,
		fetchContractorLocations,
		fetchMultipleContractorLocations,
	});
	const COMMON_FILTER_CONFIGS = useCommonFilterConfiguration({
		locations,
		fetchLocations,
		areas,
		fetchAreasDistinct,
		workOrderPriorities,
		fetchWorkOrderPriorities,
		assets,
		fetchAssets,
		fetchMultipleAssets,
		assetTypes,
		fetchAssetTypes,
		fetchMultipleAssetTypes,
		planned_maintenance_schedules,
		fetchPMSchedule,
		fetchMultiplePMSchedule,
		problemTypes,
		fetchProblemTypes,
		currentUser,
		supplierFacilities,
		fetchSupplierFacilitiesInPrivateNetwork,
		resolutionTypes,
		fetchResolutionTypes,
		fetchMultipleResolutionTypes,
		staleDays,
		updateFilters,
		users,
		fetchUsers,
		fetchMultipleUsers,
	});

	const FILTER_CONFIG = useMemo(() => {
		const defaultConfig: any[] =
			userType === ROLE_TYPES.SUPPLIER
				? [...DASHBOARD_FILTER_CONFIGS, ...COMMON_FILTER_CONFIGS, ...SUPPLIER_FILTER_CONFIG]
				: [...DASHBOARD_FILTER_CONFIGS, ...COMMON_FILTER_CONFIGS, ...BUYER_FILTER_CONFIGS];
		const advancedFilterConfig =
			filterConfig && filterConfig.filters && filterConfig.filters.length > 0
				? filterConfig.filters.map((filter) => defaultConfig.find((_) => _.fieldName === filter))
				: defaultConfig;
		return {
			...filterConfig,
			filters: advancedFilterConfig.filter((_) => !!_),
		};
	}, [userType, filterConfig, COMMON_FILTER_CONFIGS, BUYER_FILTER_CONFIGS, SUPPLIER_FILTER_CONFIG]);

	return view ? (
		<div className="flex w-full flex-row">
			<Tag
				onClose={() => setView(null)}
				closable
				key={view.name}
				closeIcon={<CloseOutlined style={{ color: 'white' }} />}
				style={{
					fontSize: '14px',
					padding: '4px 12px',
					height: '30px',
					marginTop: 1,
					marginRight: 14,
					backgroundColor: getPrimaryColor(userType),
					color: 'white',
				}}
			>
				{view.name}
			</Tag>
			<SelectedFilters
				updateFilters={(filters) => {}}
				filterValues={view.filterValue}
				filterConfig={FILTER_CONFIG.filters}
				pillsClosable={false}
			/>
		</div>
	) : null;
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	handleQueryParams: ownProps.handleQueryParams,
	locations: state.locations,
	currentUser: state.session.currentUser,
	workOrderPriorities: state.work_order_priorities,
	assetTypes: state.asset_types,
	assets: state.assets,
	contractorPrivateNetworks: state.contractor_private_networks,
	spendCategories: state.spend_categories,
	problemTypes: state.problem_types,
	regions: state.regions,
	areas: state.areas,
	resolutionTypes: state.resolution_types,
	supplierFacilities: state.supplier_facilities,
	fieldTechs: state.field_techs,
	planned_maintenance_schedules: state.planned_maintenance_schedules,
	workOrders: state.work_orders[ownProps.targetCollectionName || TARGETCOLLECTIONNAME],
	workOrderSlice: state.work_orders,
	companyConfig: state.company_config.detail,
	targetCollectionName: ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
	contractorClients: state.contractor_clients,
	contractorLocations: state.contractor_locations,
	users: state.all_contacts,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchData: (location) =>
		dispatch(
			fetchWorkOrders(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				location,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				ownProps.preAppliedFilters,
				ownProps.initialPagination,
				ownProps.initialSorting,
				ownProps.initialFilters,
				ownProps.ignoreRefreshCounts
			)
		),
	updatePagination: (pagination) =>
		dispatch(
			updatePaginationWorkOrders(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				pagination,
				ownProps.ignoreRefreshCounts
			)
		),
	updateSorting: (sorting) =>
		dispatch(
			updateSortingWorkOrders(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				sorting,
				true
			)
		),
	updateFilters: (filters) =>
		dispatch(
			updateFiltersWorkOrders(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				filters,
				ownProps.ignoreRefreshCounts
			)
		),
	replaceFiltersAndSorting: (filters, sorting) =>
		dispatch(
			replaceFiltersAndSortingWorkOrders(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				filters,
				sorting,
				ownProps.ignoreRefreshCounts
			)
		),
	clearFilters: () =>
		dispatch(
			clearFiltersWorkOrders(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				ownProps.preAppliedFilters,
				ownProps.ignoreRefreshCounts
			)
		),
	downloadWorkOrderCSV: (params, filters) =>
		dispatch(downloadWorkOrderCSV(ownProps.userType)(params, filters)),
	fetchWorkOrderPriorities: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) => {
		const fnToCall =
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? workOrderPrioritiesRestCrudThunksForSupplier
				: workOrderPrioritiesRestCrudThunksForBuyer;
		return dispatch(
			fnToCall.read(
				params,
				targetCollectionName || TARGETCOLLECTIONNAME,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		);
	},
	fetchAssetTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) => {
		const fnToCall =
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? assetTypesRestCrudThunksForSupplier
				: assetTypesRestCrudThunksForBuyer;
		return dispatch(
			fnToCall.readLite(
				{ ...params, no_pagination: true },
				targetCollectionName || TARGETCOLLECTIONNAME,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		);
	},
	fetchMultipleAssetTypes: (ids, targetCollectionName) => {
		const fnToCall =
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? assetTypesRestCrudThunksForSupplier
				: assetTypesRestCrudThunksForBuyer;
		return dispatch(fnToCall.readMultiple(ids, targetCollectionName));
	},
	fetchMultipleResolutionTypes: (ids, targetCollectionName) => {
		const fnToCall =
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? resolutionTypesRestCrudThunksForSupplier
				: resolutionTypesRestCrudThunksForBuyer;
		return dispatch(fnToCall.readMultiple(ids, targetCollectionName));
	},
	fetchResolutionTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) => {
		const fnToCall =
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? resolutionTypesRestCrudThunksForSupplier
				: resolutionTypesRestCrudThunksForBuyer;
		return dispatch(
			fnToCall.read(
				params,
				targetCollectionName || TARGETCOLLECTIONNAME,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		);
	},
	fetchProblemTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) => {
		const fnToCall =
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? problemTypesHeirarchicalForSupplier
				: problemTypesHeirarchicalForBuyer;
		return dispatch(
			fnToCall(
				params,
				targetCollectionName || TARGETCOLLECTIONNAME,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		);
	},
	fetchSupplierFacilitiesInPrivateNetwork: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) => {
		const fnToCall =
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? fetchSupplierFacilitiesInPrivateNetworkForSupplier
				: fetchSupplierFacilitiesInPrivateNetworkForBuyer;
		return dispatch(
			fnToCall(
				params,
				targetCollectionName || TARGETCOLLECTIONNAME,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		);
	},
	fetchPMSchedule: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) => {
		const fnToCall =
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? plannedMaintenanceSchedulesRestCrudThunksForSupplier
				: plannedMaintenanceSchedulesRestCrudThunksForBuyer;
		return dispatch(
			fnToCall.read(
				params,
				targetCollectionName || TARGETCOLLECTIONNAME,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		);
	},
	fetchMultiplePMSchedule: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) => {
		const fnToCall =
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? plannedMaintenanceSchedulesRestCrudThunksForSupplier
				: plannedMaintenanceSchedulesRestCrudThunksForBuyer;
		return dispatch(
			fnToCall.readMultiple(
				params,
				targetCollectionName || TARGETCOLLECTIONNAME,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		);
	},
	fetchMultipleFieldTechs: (ids, targetCollectionName) =>
		dispatch(fieldTechsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchFieldTechs: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			fieldTechsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchSpendCategories: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			spendCategoriesRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleSpendCategories: (ids, targetCollectionName) =>
		dispatch(spendCategoriesRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchAssets: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? assetsRestCrudThunksForSupplier.readLite(
						{ ...params },
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: assetsRestCrudThunksForBuyer.readLite(
						{ ...params },
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	fetchMultipleAssets: (ids, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? assetsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)
				: assetsRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)
		),
	fetchContractors: (params, targetCollectionName) =>
		dispatch(ContractorPrivateNetworksRestCrudThunksForSupplier.read(params, targetCollectionName)),
	fetchAreasDistinct: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			fetchDistinctAreasHeirarchy(ownProps.userType)(
				{ ...params, no_pagination: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchAreaCount: (params = {}) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? areasRestCrudThunksForSupplier.countBy(params, 'all')
				: areasRestCrudThunksForBuyer.countBy(params, 'all')
		),
	fetchContractorClients: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? contractorClientsRestCrudThunksForSupplier.read(
						{ ...params },
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: contractorClientsRestCrudThunksForBuyer.read(
						{ ...params },
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	fetchMultipleContractorClients: (ids, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? contractorClientsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)
				: contractorClientsRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)
		),
	fetchContractorLocations: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? contractorLocationsRestCrudThunksForSupplier.read(
						{ ...params },
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: contractorLocationsRestCrudThunksForBuyer.read(
						{ ...params },
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	fetchMultipleContractorLocations: (ids, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? contractorLocationsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)
				: contractorLocationsRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)
		),

	fetchUsers: (params, targetCollectionName, pagination, sorting, filters, addToTargetCollection) =>
		dispatch(
			isBuyerOrInternalTech(ownProps.currentUser)
				? getContactsRestCrudThunksWithInternalTechs(ownProps.userType).read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection,
						(data) => data.map((user) => ({ email: user.id, ...user }))
				  )
				: contactsRestCrudThunksForSupplierForThirdPartySuppliers.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection,
						(data) => data.map((user) => ({ email: user.id, ...user }))
				  )
		),
	fetchMultipleUsers: (
		emails,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			isBuyerOrInternalTech(ownProps.currentUser)
				? getContactsRestCrudThunksWithInternalTechs(ownProps.userType).read(
						{ emails },
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection,
						(data) => data.map((user) => ({ email: user.id, ...user }))
				  )
				: contactsRestCrudThunksForSupplierForThirdPartySuppliers.read(
						{ emails },
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection,
						(data) => data.map((user) => ({ email: user.id, ...user }))
				  )
		),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(AttentionDashboardFilterConfig)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
		currentUser: (state as any).session.currentUser,
	}),
	() => ({})
)(ComponentWithoutUserType);
